import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { parseAsString, useQueryState } from 'nuqs'

import { UploadSource } from '@buffer-mono/uploader'

import useIdeasUploader from '~publish/hooks/useIdeasUploader'
import { useOnce } from '~publish/hooks/useOnce'
import { toQueryParamArray, useQueryParam } from '~publish/hooks/useQueryParam'
import { useSelectedTags } from '~publish/hooks/useSelectedTags'

import { useIdeaComposerVariant } from './IdeaComposerPanelContext'
import { IdeaComposer } from '../LegacyIdeaComposer/IdeaComposer'
import type { NewIdea } from '../LegacyIdeaComposer/IdeaComposer'

import type { JSX } from 'react'

/**
 * Component used to open IdeaComposer with preselected data.
 * Renders when corresponding route is matched.
 */
export function IdeaCreationDialog(): JSX.Element | null {
  const [open, setOpen] = React.useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const variant = useIdeaComposerVariant()
  const locationState = location.state
  const returnTo =
    typeof locationState === 'object' &&
    locationState !== null &&
    'returnTo' in locationState &&
    typeof locationState.returnTo === 'string'
      ? locationState.returnTo
      : undefined

  const [text] = useQueryState('text', parseAsString)
  const [title] = useQueryState('title', parseAsString)
  const [groupId] = useQueryState('groupId', parseAsString)
  const selectedTags = useSelectedTags()

  const initialIdea: NewIdea = {
    groupId: groupId ?? undefined,
    content: {
      title: title ?? undefined,
      text: text ?? undefined,
      tags: selectedTags.map(({ id, name, color }) => ({ id, name, color })),
      services: [],
    },
  }

  // Preload any media passed in query params. This URL is built by an
  // external producer (the browser extension), not by this app, so it's read
  // through the legacy hook + a defensive coercion rather than nuqs — nuqs's
  // array format is only guaranteed to round-trip with URLs this app itself
  // writes.
  const [rawMedia] = useQueryParam<string[]>('media')
  const media = toQueryParamArray(rawMedia)
  const uploader = useIdeasUploader()

  // Only need to run once - media is only provided in
  // initial query param
  useOnce((): boolean => {
    if (media) {
      void uploader.addFilesFromUrls(
        media.map((url) => ({
          remoteUrl: url,
          useResizer: true,
        })),
        {
          source: UploadSource.extension(),
        },
      )
    }
    return !!media
  }, [media, uploader])

  /**
   * When the composer is closed, we want to navigate back to the original page
   * that opened the composer to ensure a seamless experience.
   *
   * We use the `returnTo` path stored in location state when the dialog was opened.
   * This ensures we navigate back to the correct page regardless of where the
   * dialog was opened from (e.g., /create or /create/ideas).
   *
   * Keeping the `view` search param prevents re-rendering the /create page,
   * since without params the page will auto-add the ?view=board and thus cause
   * a flash/re-render + resetting scroll, which is not what we want.
   */
  const navigateBack = React.useCallback((): void => {
    const cleanedParams = new URLSearchParams()
    const viewParam = new URLSearchParams(location.search).get('view')
    if (viewParam !== null) {
      cleanedParams.set('view', viewParam)
    }

    navigate({
      pathname: returnTo ?? '..',
      search: cleanedParams.toString(),
    })
  }, [navigate, location.search, returnTo])

  const handleOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen)
    // The panel variant navigates from onExited instead, after its exit
    // animation finishes; navigating here would unmount it instantly.
    if (!nextOpen && variant === 'dialog') {
      navigateBack()
    }
  }

  return (
    <IdeaComposer
      initialIdea={initialIdea}
      open={open}
      onOpenChange={handleOpenChange}
      uploader={uploader}
      variant={variant}
      onExited={navigateBack}
    />
  )
}
